import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, Phone, Building2, DollarSign, Send, Calendar as CalendarIcon,
  UserPlus, FolderPlus, FileText, Receipt, Check, Clock, MessageSquare,
} from "lucide-react";

const KIND_ICON = {
  "lead.created": Clock, "quote.drafted": FileText, "quote.sent": Send,
  "quote.approved": Check, "quote.rejected": Check, "lead.converted": UserPlus,
  "file.uploaded": FolderPlus, "renewals.manual_run": CalendarIcon,
};

export default function LeadDetail() {
  const { lead_id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/crm/leads/${lead_id}/timeline`);
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load lead");
    } finally { setLoading(false); }
  }, [lead_id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <DashboardLayout title="Lead"><div className="text-slate-500 text-sm p-8">Loading…</div></DashboardLayout>;
  if (!data) return <DashboardLayout title="Lead"><div className="p-8 text-slate-500">Lead not found.</div></DashboardLayout>;

  const { lead, events, quote, task, files } = data;
  const isConverted = lead.status === "converted";

  const doAction = async (label, fn) => {
    setBusy(label);
    try { await fn(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || `${label} failed`); }
    finally { setBusy(null); }
  };

  const sendQuote = async () => {
    if (!quote) { toast.error("No draft quote yet"); return; }
    // Navigate to /app/quotes and let the existing editor take over (PDF + email + status).
    navigate(`/app/quotes?open=${quote.quote_id}`);
  };

  const scheduleConsult = () => doAction("schedule", async () => {
    await api.post("/tasks", {
      title: `Consultation call — ${lead.name}`, priority: "high", status: "todo",
      description: `Follow-up consult scheduled from lead ${lead_id}`,
    });
    toast.success("Consultation task created");
    load();
  });

  const convert = () => doAction("convert", async () => {
    const { data: r } = await api.post(`/crm/leads/${lead_id}/convert`);
    toast.success(`Converted — client ${r.client_id?.slice(-6)} + project ${r.project_id?.slice(-6)}`);
    load();
  });

  const createProject = () => doAction("project", async () => {
    if (!lead.converted_client_id) { toast.error("Convert to customer first"); return; }
    const { data: p } = await api.post("/projects", {
      name: `${lead.service_needed || "New project"} — ${lead.name}`,
      client_id: lead.converted_client_id, kind: "generic", status: "planning",
    });
    toast.success("Project created");
    navigate(`/app/projects/kanban#${p.project_id}`);
  });

  const sendInvoice = () => doAction("invoice", async () => {
    if (!lead.converted_client_id) { toast.error("Convert to customer first"); return; }
    navigate(`/app/invoices?client=${lead.converted_client_id}`);
  });

  return (
    <DashboardLayout
      title={lead.name}
      subtitle={`Lead · ${lead.pipeline_stage || lead.status}`}
      right={
        <Link to="/app/leads" data-testid="lead-back" className="btn-scale h-9 px-3 rounded-md border border-slate-700 text-slate-300 text-sm inline-flex items-center gap-2 hover:bg-slate-800">
          <ArrowLeft className="w-4 h-4" /> All leads
        </Link>
      }
    >
      {/* Info card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="lead-info">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-4">Lead information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <Info Icon={Mail} label="Email" value={lead.email} />
            <Info Icon={Phone} label="Phone" value={lead.phone} />
            <Info Icon={Building2} label="Company" value={lead.company} />
            <Info Icon={DollarSign} label="Budget" value={lead.budget} />
            <Info Icon={FileText} label="Service" value={lead.service_needed} />
            <Info Icon={Clock} label="Source" value={lead.source} />
          </div>
          {lead.message && (
            <div className="mt-5 pt-5 border-t border-zinc-800">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Project description</div>
              <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{lead.message}</p>
            </div>
          )}
        </div>

        {/* One-click sales actions */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="lead-actions">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-4">One-click actions</h3>
          <div className="space-y-2">
            <ActionBtn testid="act-send-quote" onClick={sendQuote} disabled={!quote || busy}
              icon={Send} label="Send Quote" hint={quote ? `Opens draft: ${(quote.title||'').slice(0,32)}…` : "No draft yet"} />
            <ActionBtn testid="act-schedule" onClick={scheduleConsult} disabled={busy}
              icon={CalendarIcon} label="Schedule Consultation" hint="Creates high-priority task" />
            <ActionBtn testid="act-convert" onClick={convert} disabled={isConverted || busy}
              icon={UserPlus} label={isConverted ? "Already converted" : "Convert to Customer"}
              hint={isConverted ? `Client ${lead.converted_client_id?.slice(-6)}` : "Creates client + project"} primary />
            <ActionBtn testid="act-project" onClick={createProject} disabled={busy}
              icon={FolderPlus} label="Create Project" hint={isConverted ? "Kanban board" : "Convert first"} />
            <ActionBtn testid="act-contract" onClick={() => toast.info("Contract templates — coming soon")} disabled={busy}
              icon={FileText} label="Send Contract" hint="From /app/contracts (records)" />
            <ActionBtn testid="act-invoice" onClick={sendInvoice} disabled={busy}
              icon={Receipt} label="Send Invoice" hint={isConverted ? "Open invoices" : "Convert first"} />
          </div>
        </div>
      </div>

      {/* Timeline + related */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="lead-timeline">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-5">Timeline</h3>
          <ol className="relative border-l border-zinc-800 pl-6 space-y-4">
            {events.map((e, i) => {
              const Icon = KIND_ICON[e.kind] || Clock;
              return (
                <li key={`${e.at}-${i}`} className="relative" data-testid={`tl-${i}`}>
                  <span className="absolute -left-[27px] w-4 h-4 grid place-items-center rounded-full bg-blue-500/20 border border-blue-500/40">
                    <Icon className="w-2.5 h-2.5 text-blue-300" />
                  </span>
                  <div className="text-sm text-zinc-100">{e.label}</div>
                  <div className="text-[11px] text-zinc-500 font-mono mt-0.5">{(e.at || "").slice(0, 19).replace("T", " ")} · {e.kind}</div>
                </li>
              );
            })}
            {events.length === 0 && <li className="text-sm text-zinc-500">No events yet.</li>}
          </ol>
        </div>

        <div className="space-y-4">
          {quote && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="lead-draft-quote">
              <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Draft quote</h3>
              <div className="text-sm text-zinc-100 mb-1 truncate">{quote.title}</div>
              <div className="text-xs text-zinc-500">Status: {quote.status} · Total: ${Number(quote.total || 0).toFixed(2)}</div>
              <button onClick={sendQuote} className="mt-3 btn-scale w-full h-9 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-xs font-medium">Open in Quotes editor</button>
            </div>
          )}
          {task && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="lead-task">
              <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Follow-up task</h3>
              <div className="text-sm text-zinc-100 mb-1">{task.title}</div>
              <div className="text-xs text-zinc-500">Due: {task.due_date || "—"} · {task.status}</div>
            </div>
          )}
          {files && files.length > 0 && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="lead-files">
              <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Attached files ({files.length})</h3>
              <ul className="text-sm text-zinc-300 space-y-1">
                {files.slice(0, 5).map((f) => (<li key={f.file_id} className="truncate flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-blue-400" /> {f.original_filename}</li>))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function Info({ Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-8 h-8 grid place-items-center rounded-md bg-blue-500/10 text-blue-400 shrink-0"><Icon className="w-4 h-4" /></span>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="text-sm text-zinc-100 truncate">{value || "—"}</div>
      </div>
    </div>
  );
}

function ActionBtn({ testid, onClick, disabled, icon: Icon, label, hint, primary }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className={`w-full text-left rounded-lg border p-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        primary ? "bg-blue-500/10 border-blue-500/40 hover:bg-blue-500/20" : "border-zinc-800 bg-zinc-950/50 hover:border-zinc-700 hover:bg-zinc-900"
      }`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${primary ? "text-blue-300" : "text-blue-400"}`} />
        <span className="text-sm font-medium text-zinc-100">{label}</span>
      </div>
      {hint && <div className="text-[11px] text-zinc-500 mt-0.5 pl-6 truncate">{hint}</div>}
    </button>
  );
}
